// =============================================================================
// FILE PATH: src/modules/pms/components/WBSFormModal.js
// =============================================================================

import React, { useEffect, useState } from "react";
import { getAssignableBusinessPartners } from "../services/businessPartnerService";

export default function WBSFormModal({
  open,
  onClose,
  onSave,
  parentId,
  projectId,

  // 🔥 ADDED (EDIT SUPPORT)
  editNode = null,
  isEdit = false
}) {

  const [form, setForm] = useState({
    name: "",
    type: parentId ? "TASK" : "ITEM",
    assigned_to: "",
    planned_start: "",
    planned_end: ""
  });

  const [error, setError] = useState("");
  const [bpList, setBpList] = useState([]);

  useEffect(() => {
    if (open) {
      getAssignableBusinessPartners()
        .then(res => {
          setBpList(res.data.data || []);
        })
        .catch(err => {
          console.error("Failed to load BP list", err);
        });
    }
  }, [open]);

  // ---------------------------------------------------------
  // RESET FORM WHEN OPEN (UNCHANGED)
  // ---------------------------------------------------------
  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        type: parentId ? "TASK" : "ITEM",
        assigned_to: "",
        planned_start: "",
        planned_end: ""
      });
      setError("");
    }
  }, [open, parentId]);

  // ---------------------------------------------------------
  // 🔥 NEW: PREFILL FORM FOR EDIT (ADDED ONLY)
  // ---------------------------------------------------------
  useEffect(() => {
    if (open && isEdit && editNode) {
      setForm({
        name: editNode.name || "",
        type: editNode.type || "TASK",
        assigned_to: editNode.assigned_to || "",
        planned_start: editNode.planned_start || "",
        planned_end: editNode.planned_end || ""
      });
    }
  }, [open, isEdit, editNode]);

  if (!open) return null;

  // ---------------------------------------------------------
  // SUBMIT
  // ---------------------------------------------------------
  const handleSubmit = () => {

    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }

    // 🔥 DATE VALIDATION
    if (form.planned_start && form.planned_end) {
      if (form.planned_start > form.planned_end) {
        setError("Start date cannot be after End date");
        return;
      }
    }

    // 🔥 EDIT MODE (ADDED)
    if (isEdit) {
      onSave({
        node_id: editNode.id,
        name: form.name,
        type: form.type,
        assigned_to: form.type === "TASK" ? form.assigned_to : null,
        planned_start: form.planned_start || null,
        planned_end: form.planned_end || null
      });
      return;
    }

    // 🔥 ORIGINAL CREATE FLOW (UNCHANGED)
    onSave({
      project_id: projectId,
      parent_id: parentId,
      name: form.name,
      type: form.type,
      assigned_to: form.type === "TASK" ? form.assigned_to : null,
      planned_start: form.planned_start || null,
      planned_end: form.planned_end || null
    });
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* HEADER */}
        <h3 style={styles.title}>
          {isEdit ? "Edit WBS Node" : "Add WBS Node"}
        </h3>

        {/* NAME */}
        <div style={styles.field}>
          <label style={styles.label}>Name</label>
          <input
            style={styles.input}
            placeholder="Enter name"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
          />
        </div>

        {/* TYPE */}
        <div style={styles.field}>
          <label style={styles.label}>Type</label>

          <select
            style={styles.input}
            value={form.type}
            onChange={(e) =>
              setForm({ ...form, type: e.target.value })
            }
          >

            {/* ROOT LEVEL */}
            {!parentId && (
              <>
                <option value="ITEM">Item</option>
                <option value="PHASE">Phase</option>
              </>
            )}

            {/* CHILD LEVEL */}
            {parentId && (
              <>
                <option value="PHASE">Phase</option>
                <option value="TASK">Task</option>
              </>
            )}

          </select>
        </div>

        {/* ASSIGNED */}
        <div style={styles.field}>
          <label style={styles.label}>Assigned To</label>

          <select
            style={styles.input}
            value={form.assigned_to || ""}
            disabled={
              form.type === "PHASE" ||
              form.type === "ITEM"
            }
            onChange={(e) =>
              setForm({ ...form, assigned_to: e.target.value })
            }
          >
            <option value="">-- Select Assignee --</option>

            {bpList.map(bp => (
              <option key={bp.id} value={bp.id}>
                {bp.name} ({bp.role})
              </option>
            ))}
          </select>
        </div>

        {/* 🔥 PLANNED START */}
        <div style={styles.field}>
          <label style={styles.label}>Planned Start</label>

          <input
            type="date"
            style={styles.input}
            value={form.planned_start}
            onChange={(e) =>
              setForm({ ...form, planned_start: e.target.value })
            }
          />
        </div>

        {/* 🔥 PLANNED END */}
        <div style={styles.field}>
          <label style={styles.label}>Planned End</label>

          <input
            type="date"
            style={styles.input}
            value={form.planned_end}
            onChange={(e) =>
              setForm({ ...form, planned_end: e.target.value })
            }
          />
        </div>

        {/* ERROR */}
        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {/* ACTIONS */}
        <div style={styles.actions}>

          <button
            style={styles.cancelBtn}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            style={styles.saveBtn}
            onClick={handleSubmit}
          >
            {isEdit ? "Update" : "Save"}
          </button>

        </div>

      </div>
    </div>
  );
}

// ==========================================================
// STYLES (UNCHANGED)
// ==========================================================
const styles = {

  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999
  },

  modal: {
    background: "#fff",
    padding: 20,
    borderRadius: 8,
    width: 320,
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
  },

  title: {
    marginBottom: 15
  },

  field: {
    marginBottom: 12
  },

  label: {
    display: "block",
    fontSize: 12,
    marginBottom: 4,
    color: "#374151"
  },

  input: {
    width: "100%",
    padding: "8px",
    border: "1px solid #d1d5db",
    borderRadius: 4,
    fontSize: 13,
    boxSizing: "border-box"
  },

  error: {
    color: "#dc2626",
    fontSize: 12,
    marginBottom: 10
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10
  },

  saveBtn: {
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: 4,
    cursor: "pointer"
  },

  cancelBtn: {
    background: "#e5e7eb",
    border: "none",
    padding: "6px 12px",
    borderRadius: 4,
    cursor: "pointer"
  }
};